import { memo, useMemo } from 'react';
import { Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type Big from 'big.js';

type AvailabilityIndicatorProps = {
    total: Big;
    availability: number;
};

const useIndicatorStyles = makeStyles({
    dot: {
        display: 'inline-block',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        marginRight: '6px',
        cursor: 'help',
        flexShrink: 0,
    },
    green: {
        backgroundColor: tokens.colorStatusSuccessForeground1,
    },
    amber: {
        backgroundColor: tokens.colorStatusWarningForeground1,
    },
    red: {
        backgroundColor: tokens.colorStatusDangerForeground1,
    },
    tooltipGrid: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: '8px',
        rowGap: '2px',
    },
});

type RagColor = 'green' | 'amber' | 'red';

const RAG_GREEN_LOW = 0.6;
const RAG_GREEN_HIGH = 1.4;
const RAG_AMBER_LOW = 0.4;
const RAG_AMBER_HIGH = 1.6;

const getRagColor = (total: number, availability: number): RagColor => {
    if (total >= availability * RAG_GREEN_LOW && total <= availability * RAG_GREEN_HIGH) return 'green';
    if (total >= availability * RAG_AMBER_LOW && total <= availability * RAG_AMBER_HIGH) return 'amber';
    return 'red';
};

export const AvailabilityIndicator = memo(function AvailabilityIndicator({ total, availability }: AvailabilityIndicatorProps) {
    const { t } = useTranslation('timesheet');
    const styles = useIndicatorStyles();

    const totalNumber = total.toNumber();
    const color = getRagColor(totalNumber, availability);
    const totalFormatted = total.round(2).toString();

    const tooltipContent = useMemo(
        () => (
            <div style={{ textAlign: 'left' }}>
                <div>{t('table.availability.title', { value: availability })}</div>
                <div>{t('table.availability.description', { value: totalFormatted })}</div>
                <br />
                <div className={styles.tooltipGrid}>
                    <span>{t('table.red.title')}</span>
                    <span>
                        {t('table.red.description', {
                            value: (availability * RAG_AMBER_LOW).toFixed(1),
                            value2: (availability * RAG_AMBER_HIGH).toFixed(1),
                        })}
                    </span>
                    <span>{t('table.amber.title')}</span>
                    <span>
                        {t('table.amber.description', {
                            value: (availability * RAG_AMBER_LOW).toFixed(1),
                            value2: (availability * RAG_GREEN_LOW - 0.1).toFixed(1),
                            value3: (availability * RAG_GREEN_HIGH + 0.1).toFixed(1),
                            value4: (availability * RAG_AMBER_HIGH).toFixed(1),
                        })}
                    </span>
                    <span>{t('table.green.title')}</span>
                    <span>
                        {t('table.green.description', {
                            value: (availability * RAG_GREEN_LOW).toFixed(1),
                            value2: (availability * RAG_GREEN_HIGH).toFixed(1),
                        })}
                    </span>
                </div>
            </div>
        ),
        [t, availability, totalFormatted, styles.tooltipGrid],
    );

    return (
        <Tooltip content={tooltipContent} relationship="description" withArrow hideDelay={250}>
            <span className={mergeClasses(styles.dot, styles[color])} data-testid="availabilityStatusIndicator" />
        </Tooltip>
    );
});
